import * as Slider from "@radix-ui/react-slider";
import { cn } from "@/lib/cn";
import type { CapacityLevel } from "@/lib/capacityLevel";

interface LevelStyle {
  range: string;
  thumb: string;
  glow: string;
}

const STYLE: Record<CapacityLevel, LevelStyle> = {
  GREEN: {
    range: "bg-capacity-green light:bg-capacity-green-light",
    thumb: "border-capacity-green light:border-capacity-green-light",
    glow: "shadow-[0_0_12px_-2px_rgb(0_0_0)] shadow-capacity-green/60 light:shadow-capacity-green-light/50",
  },
  YELLOW: {
    range: "bg-capacity-yellow light:bg-capacity-yellow-light",
    thumb: "border-capacity-yellow light:border-capacity-yellow-light",
    glow: "shadow-[0_0_12px_-2px_rgb(0_0_0)] shadow-capacity-yellow/55 light:shadow-capacity-yellow-light/50",
  },
  RED: {
    range: "bg-capacity-red light:bg-capacity-red-light",
    thumb: "border-capacity-red light:border-capacity-red-light",
    glow: "shadow-[0_0_12px_-2px_rgb(0_0_0)] shadow-capacity-red/55 light:shadow-capacity-red-light/50",
  },
};

export interface CapacitySliderProps {
  occupied: number;
  capacity: number;
  level: CapacityLevel;
  className?: string;
  ariaLabel?: string;
}

/**
 * Read-only Radix slider visualizing how full a restaurant is.
 *
 * Animations:
 *   - Range width and color tween together over 600ms (ease-out) when the
 *     occupancy or level changes — so live POS updates feel like motion,
 *     not jumps.
 *   - Thumb position animates via the same transition (Radix uses inline
 *     `transform`/`left`, both covered by `transition-all`).
 */
export function CapacitySlider({
  occupied,
  capacity,
  level,
  className,
  ariaLabel,
}: CapacitySliderProps) {
  const safeCapacity = Math.max(capacity, 1);
  const value = Math.min(Math.max(occupied, 0), safeCapacity);
  const styles = STYLE[level];

  return (
    <Slider.Root
      className={cn(
        "relative flex h-5 w-full select-none items-center",
        className,
      )}
      value={[value]}
      max={safeCapacity}
      min={0}
      step={1}
      disabled
    >
      <Slider.Track className="relative h-1.5 grow overflow-hidden rounded-full bg-zinc-800 light:bg-zinc-200">
        <Slider.Range
          className={cn(
            "absolute h-full rounded-full transition-all duration-700 ease-out motion-reduce:transition-none",
            styles.range,
          )}
        />
      </Slider.Track>
      {/* Radix puts role="slider" on the thumb, so the name goes here. */}
      <Slider.Thumb
        aria-label={ariaLabel ?? `${value} of ${safeCapacity} tables occupied`}
        className={cn(
          "block h-4 w-4 rounded-full border-2 bg-zinc-950 light:bg-white transition-all duration-700 ease-out motion-reduce:transition-none",
          styles.thumb,
          styles.glow,
        )}
      />
    </Slider.Root>
  );
}
